/* TR Shoppable reels: sections/tr-shoppable-reels.liquid
   - Lazy video: src is set only when a reel is 50% on screen; paused when it leaves
   - Muted previews loop the first PREVIEW_SECONDS only (little buffering)
   - Tap a reel → full clip with sound (all other reels mute); tap again → back to preview
   - No autoplay with Data Saver or prefers-reduced-motion (tap to play)
   - Desktop arrows scroll by the visible width */

if (!customElements.get('tr-shoppable-reels')) {
  class TrShoppableReels extends HTMLElement {
    static PREVIEW_SECONDS = 4;

    connectedCallback() {
      this.track = this.querySelector('[data-tr-reels-track]');
      this.medias = [...this.querySelectorAll('[data-tr-reel-media]')];
      this.autoplay =
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData;

      this.medias.forEach((media) => this.initReel(media));
      this.initObserver();
      this.initArrows();
    }

    initReel(media) {
      const video = media.querySelector('video');
      const card = media.closest('.tr-reels__card');
      media.video = video;
      media.card = card;

      video.addEventListener('playing', () => card.classList.add('is-playing'), { once: true });
      video.addEventListener('timeupdate', () => {
        if (!media.full && video.currentTime >= TrShoppableReels.PREVIEW_SECONDS) video.currentTime = 0;
      });

      media.addEventListener('click', () => this.toggleSound(media));
      media.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.toggleSound(media);
      });
    }

    load(video) {
      if (video.src) return;
      video.src = video.dataset.src;
      video.preload = 'auto';
    }

    initObserver() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            const video = target.video;
            if (isIntersecting) {
              if (!this.autoplay && !target.full) return;
              this.load(video);
              video.play().catch(() => {});
            } else if (video.src) {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );
      this.medias.forEach((media) => this.observer.observe(media));
    }

    toggleSound(media) {
      if (media.full) {
        this.toPreview(media);
        if (!this.autoplay) media.video.pause();
        return;
      }
      this.medias.forEach((other) => {
        if (other !== media && other.full) this.toPreview(other);
      });

      const video = media.video;
      this.load(video);
      media.full = true;
      video.currentTime = 0;
      video.muted = false;
      video.play().catch(() => {});
      media.card.classList.add('is-unmuted');
      media.setAttribute('aria-pressed', 'true');
    }

    toPreview(media) {
      const video = media.video;
      media.full = false;
      video.muted = true;
      if (video.currentTime > TrShoppableReels.PREVIEW_SECONDS) video.currentTime = 0;
      if (!this.autoplay) video.pause();
      media.card.classList.remove('is-unmuted');
      media.setAttribute('aria-pressed', 'false');
    }

    initArrows() {
      const prev = this.querySelector('[data-tr-reels-prev]');
      const next = this.querySelector('[data-tr-reels-next]');
      if (!this.track || !prev || !next) return;

      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        prev.disabled = this.track.scrollLeft <= 2;
        next.disabled = this.track.scrollLeft >= max;
      };

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            update();
            ticking = false;
          });
        },
        { passive: true }
      );

      const step = () => {
        const slide = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perView = Math.max(1, Math.floor(this.track.clientWidth / (slide.offsetWidth + gap)));
        return perView * (slide.offsetWidth + gap);
      };

      prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  customElements.define('tr-shoppable-reels', TrShoppableReels);
}
